import { useContext, useEffect, useMemo, useState } from 'react';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container'
import PartnerService from '../../../shared/services/partner/partner.service';
import { useNavigate, useSearchParams } from 'react-router-dom';
import gaiaTheme from '../../../root-components/theme';
import { Box, Chip, Grid, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { LoginOutlined } from '@mui/icons-material';
import moment from 'moment';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { SearchBar } from '../../presentation-components/display-elements-components/search-bar';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TooltipHover } from '../../presentation-components/miscellaneous-components/tooltip-hover';
import { AnalyticMetric } from '../../presentation-components/analytics-components/graphs/analytic-metric';
import { ReadinessStateChip } from '../../presentation-components/readiness-components/readiness-state-chip';
import { ReadinessSeverityCounts } from '../../presentation-components/readiness-components/readiness-severity-counts';
import { READINESS_STATE_LABELS } from '../../presentation-components/readiness-components/readiness-labels';
import { LoadingPage } from '../loading-page';
import { PartnerAddClientDialog } from '../../dialogs/partner/partner-add-client-dialog';
import { IPartnerBookRollup, IPartnerClientOverviewRow } from '../../../shared/services/partner/partner.model';
import { ReadinessState } from '../../../shared/services/readiness/readiness.model';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { segmentedToggleSx } from '../../../shared/utils/segmented-toggle-sx';
import { useDebounce } from '../../../hooks/use-debounce-hook';
import { useSwitchClient } from '../../../hooks/use-switch-client';

const formatTonnes = (value: number) => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} tCO₂e`

// Ink chip styling: the readiness palette's "work to do" colour, used where red would read as a
// failure (a passed deadline, an inactive subscription, an overdue count).
const INK_CHIP_SX = { borderColor: 'text.primary', color: 'text.primary' }

const READINESS_STATES: ReadinessState[] = ['not_ready', 'reportable_with_caveats', 'audit_ready']
// Matches the server-side sort rank: not ready first, no evaluation last.
const readinessRank = (client: IPartnerClientOverviewRow): number => {
  const index = client.readiness ? READINESS_STATES.indexOf(client.readiness.state) : -1
  return index === -1 ? READINESS_STATES.length : index
}

const subscriptionStatus = (client: IPartnerClientOverviewRow): 'active' | 'trial' | 'inactive' => {
  if (client.subscriptionActive) return 'active'
  if (client.freeTrialDaysRemaining > 0) return 'trial'
  return 'inactive'
}

const EMPTY_ROLLUP: IPartnerBookRollup = {
  auditReady: 0, reportableWithCaveats: 0, notReady: 0, noActivePeriod: 0, readinessUnavailable: 0, deadlinePassed: 0, overdueTasks: 0,
}

export const PartnerAllClientsPage = () => {
  const navigate = useNavigate();
  //Services
  const partnerService = new PartnerService()
  //Contexts
  const userNotificationsContext = useContext(UserNotificationsContext)
  //Switching
  const { switchToClient, isSwitching, switchingClientId } = useSwitchClient()
  //State
  const [clients, setClients] = useState<IPartnerClientOverviewRow[]>([]);
  const [rollup, setRollup] = useState<IPartnerBookRollup>(EMPTY_ROLLUP)
  const [open, setOpen] = useState(false)
  //Filter State: search and filters live in the URL so the view survives opening a
  //client and coming back, and can be shared as a link.
  const [searchParams, setSearchParams] = useSearchParams()
  const readinessFilter = searchParams.get('readiness') ?? ''
  const deadlineFilter = searchParams.get('deadline') ?? ''
  const subscriptionFilter = searchParams.get('subscription') ?? ''
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') ?? '')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [perPage, setPerPage] = useState(25)
  //Loading State
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Layout State
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  useEffect(() => {
    getClients()
  }, [])

  //Push the settled search text into the URL without stacking history entries.
  useEffect(() => {
    const current = searchParams.get('search') ?? ''
    if (current === debouncedSearchQuery) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (debouncedSearchQuery) {
        next.set('search', debouncedSearchQuery)
      } else {
        next.delete('search')
      }
      return next
    }, { replace: true })
  }, [debouncedSearchQuery])

  //React to searchParams changes (back/forward, in-app links), not just on mount.
  useEffect(() => {
    const current = searchParams.get('search') ?? ''
    if (current !== debouncedSearchQuery) {
      setSearchQuery(current)
    }
  }, [searchParams])

  const setFilterParam = (key: string, value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value) {
        next.set(key, value)
      } else {
        next.delete(key)
      }
      return next
    }, { replace: true })
  }

  //A tile click applies its filter; clicking the already-active tile clears it.
  const toggleFilterParam = (key: string, value: string) => {
    setFilterParam(key, searchParams.get(key) === value ? '' : value)
  }

  //The overview returns the whole client book with its computed columns, so search,
  //filtering, sorting and pagination are client side. Fine at partner-book scale
  //(tens of clients).
  const getClients = () => {
    partnerService.getClientsOverview()
      .then(response => {
        setClients(response.rows)
        setRollup(response.rollup)
        setIsLoading(false)
      })
      .catch(err => {
        console.log(err)
        setClients([])
        setRollup(EMPTY_ROLLUP)
        setIsLoading(false)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error loading your clients, please try again', backgroundColor: 'text.primary' })
      })
  }

  //Readiness: the client's own headline state for the active period, with the per-severity
  //counts beneath so the partner sees why without opening the account.
  const readinessCell = (client: IPartnerClientOverviewRow) => {
    if (!client.activePeriod) {
      return <Typography variant='body2' sx={{ color: 'text.secondary' }}>No active period</Typography>
    }
    if (!client.readiness) {
      return <Typography variant='body2' sx={{ color: 'text.secondary' }}>Not evaluated</Typography>
    }
    return (
      <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
        <ReadinessStateChip state={client.readiness.state} size='small' />
        <ReadinessSeverityCounts counts={client.readiness.counts} />
      </Stack>
    )
  }

  const openTasksCell = (client: IPartnerClientOverviewRow) => {
    if (!client.openTasks) return '—'
    return (
      <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
        <Typography variant='body2'>{client.openTasks.count}</Typography>
        {client.openTasks.overdueCount > 0 &&
          <Typography variant='caption' sx={{ color: 'text.primary' }}>{client.openTasks.overdueCount} overdue</Typography>}
      </Stack>
    )
  }

  //Deadline chips derive from the reporting deadline the client set on the period. The
  //readiness palette applies: a passed deadline is work to do (ink), not a failure (red).
  const deadlineCell = (client: IPartnerClientOverviewRow) => {
    if (!client.activePeriod) return '—'
    const { deadlines } = client
    if (deadlines.noDeadlineSet || deadlines.daysUntilDeadline === null) {
      return <Chip size='small' variant='outlined' label='No deadline set' />
    }
    if (deadlines.deadlinePassedUnlocked) {
      return <Chip size='small' variant='outlined' label='Deadline passed, not locked' sx={INK_CHIP_SX} />
    }
    if (deadlines.daysUntilDeadline < 0) {
      return <Chip size='small' variant='outlined' label={`Deadline ${moment(deadlines.reportingDeadline).format('D MMM YYYY')}`} />
    }
    if (deadlines.daysUntilDeadline === 0) {
      return <Chip size='small' variant='outlined' color='info' label='Deadline today' />
    }
    return <Chip size='small' variant='outlined' color='info' label={`Deadline in ${deadlines.daysUntilDeadline} ${deadlines.daysUntilDeadline === 1 ? 'day' : 'days'}`} />
  }

  const subscriptionChip = (client: IPartnerClientOverviewRow) => {
    if (client.subscriptionActive) {
      return <Chip size='small' label='Active' variant='outlined' color='success' />
    }
    if (client.freeTrialDaysRemaining > 0) {
      return <Chip size='small' label={`Trial (${client.freeTrialDaysRemaining} days left)`} variant='outlined' />
    }
    return <Chip size='small' label='Not active' variant='outlined' sx={INK_CHIP_SX} />
  }

  //One column for whichever report documents the client actually has, so a document only a
  //subset of clients need (like the PPN 006 CRP) never claims space on everyone's row. The
  //CRP age reminder lives here too: it is a fact about the document, not a deadline.
  const reportChips = (client: IPartnerClientOverviewRow) => {
    const chips = []
    if (client.crp) {
      chips.push(
        <Chip
          key='crp'
          size='small'
          label={client.crp.status === 'GENERATED' ? 'CRP: Generated' : 'CRP: Draft'}
          color={client.crp.status === 'GENERATED' ? 'primary' : 'default'}
          variant={client.crp.status === 'GENERATED' ? 'filled' : 'outlined'}
        />
      )
    }
    if (client.deadlines.crpUpdateDue) {
      chips.push(<Chip key='crp-due' size='small' variant='outlined' color='info' label='CRP update due' />)
    }
    if (client.reportCompleted) {
      chips.push(<Chip key='ghg' size='small' label='GHG report: Completed' variant='outlined' color='success' />)
    }
    if (!chips.length) return '—'
    return <Stack direction='column' spacing={0.5} sx={{ alignItems: 'flex-start' }}>{chips}</Stack>
  }

  //Table Contents. Columns whose cells render as chips sort on hidden raw-value keys
  //(sortValue) carried on each row.
  const headCells: IHeadCell[] = [
    { id: 'name', numeric: false, label: 'Client', sortable: true, isHeading: true, includeInMobile: true },
    { id: 'readiness', numeric: false, label: 'Readiness', sortable: true, sortValue: 'readinessValue', includeInMobile: true },
    { id: 'openTasks', numeric: false, label: 'Open tasks', sortable: true, sortValue: 'openTasksValue' },
    { id: 'deadlines', numeric: false, label: 'Deadline', sortable: true, sortValue: 'deadlineValue' },
    { id: 'footprint', numeric: false, label: 'Footprint', sortable: true, sortValue: 'footprintValue' },
    { id: 'period', numeric: false, label: 'Reporting period', sortable: true, sortValue: 'periodValue' },
    { id: 'completeness', numeric: false, label: 'Scope 3 categories', sortable: false },
    { id: 'dataQuality', numeric: false, label: 'Data quality', sortable: true, sortValue: 'dataQualityValue' },
    { id: 'reports', numeric: false, label: 'Reports', sortable: false },
    { id: 'subscription', numeric: false, label: 'Subscription', sortable: true, sortValue: 'subscriptionValue' },
    { id: 'action', numeric: false, align: 'right', label: '', sortable: false },
  ];

  const filteredClients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    return clients.filter(client => {
      if (query && !(client.name ?? '').toLowerCase().includes(query)) return false
      if (readinessFilter && (!client.readiness || client.readiness.state !== readinessFilter)) return false
      if (deadlineFilter === 'passed' && !client.deadlines.deadlinePassedUnlocked) return false
      if (deadlineFilter === 'none' && !(client.activePeriod && client.deadlines.noDeadlineSet)) return false
      if (subscriptionFilter && subscriptionStatus(client) !== subscriptionFilter) return false
      return true
    })
  }, [clients, searchQuery, readinessFilter, deadlineFilter, subscriptionFilter])

  const rows = filteredClients.map(client => ({
    id: client.id,
    name: client.name || 'Unnamed client',
    readiness: readinessCell(client),
    openTasks: openTasksCell(client),
    deadlines: deadlineCell(client),
    footprint: client.footprint ? formatTonnes(client.footprint.total) : '—',
    period: client.activePeriod
      ? (
        <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant='body2'>
            {client.activePeriod.friendlyName || `${moment(client.activePeriod.startDate).format('DD/MM/YYYY')} to ${moment(client.activePeriod.endDate).format('DD/MM/YYYY')}`}
          </Typography>
          {client.activePeriod.locked && <Chip size='small' label='Locked' variant='outlined' />}
        </Stack>
      )
      : '—',
    completeness: client.completeness
      ? `${client.completeness.reported} of ${client.completeness.total}`
      : '—',
    dataQuality: client.dataQuality
      ? (
        <TooltipHover text='Share of the footprint calculated from activity data (the High and Medium data-quality tiers) rather than money spent. The same figure as the client dashboard.' placement='top' backgroundColor='common.white'>
          <Typography variant='body2' sx={{ display: 'inline-block', cursor: 'default' }}>{client.dataQuality.activitySharePct}% activity data</Typography>
        </TooltipHover>
      )
      : '—',
    reports: reportChips(client),
    subscription: subscriptionChip(client),
    //Raw values behind the chip columns; clients with no value carry a sentinel above
    //every real value, so on an ascending sort they land last rather than mixed in.
    sortKey: client.sortKey,
    readinessValue: readinessRank(client),
    openTasksValue: client.openTasks ? client.openTasks.count : -1,
    deadlineValue: client.deadlines.daysUntilDeadline === null ? Number.MAX_SAFE_INTEGER : client.deadlines.daysUntilDeadline,
    footprintValue: client.footprint ? client.footprint.total : Number.MAX_SAFE_INTEGER,
    periodValue: client.activePeriod ? moment(client.activePeriod.endDate).valueOf() : Number.MAX_SAFE_INTEGER,
    dataQualityValue: client.dataQuality ? client.dataQuality.activitySharePct : Number.MAX_SAFE_INTEGER,
    subscriptionValue: subscriptionStatus(client) === 'active' ? 0 : subscriptionStatus(client) === 'trial' ? 1 : 2,
    action: (
      <LoadingButton
        variant='outlined'
        startIcon={<LoginOutlined />}
        loading={switchingClientId === client.id}
        disabled={isSwitching}
        //Without stopPropagation the click also selects the row, which navigates to
        //the client detail page and unmounts this table mid-switch.
        onClick={(event) => { event.stopPropagation(); switchToClient(client.id) }}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
      >
        Open account
      </LoadingButton>
    ),
  }));

  //Table Event Handlers
  const handleSelected = (selectedRowId: string[]) => {
    if (selectedRowId[0] !== undefined) {
      navigate(`/partner-clients/${selectedRowId[0]}`)
    }
  }

  const successHandler = () => {
    setOpen(false)
    setIsLoading(true)
    getClients()
  }

  //Book rollup tiles: the readiness page's tile pattern, one per state plus passed deadlines.
  //A tile doubles as a filter.
  const tiles: { key: string, title: string, value: number, units: string, filterKey: 'readiness' | 'deadline', filterValue: string, tooltip: string }[] = [
    { key: 'notReady', title: READINESS_STATE_LABELS.not_ready, value: rollup.notReady, units: rollup.notReady === 1 ? 'client' : 'clients', filterKey: 'readiness', filterValue: 'not_ready', tooltip: 'Clients with at least one Critical finding on their current period. The report will be wrong or incomplete until these are fixed.' },
    { key: 'caveats', title: READINESS_STATE_LABELS.reportable_with_caveats, value: rollup.reportableWithCaveats, units: rollup.reportableWithCaveats === 1 ? 'client' : 'clients', filterKey: 'readiness', filterValue: 'reportable_with_caveats', tooltip: 'Clients with no Critical findings but open Action required findings: defensible either way, but silence is not.' },
    { key: 'auditReady', title: READINESS_STATE_LABELS.audit_ready, value: rollup.auditReady, units: rollup.auditReady === 1 ? 'client' : 'clients', filterKey: 'readiness', filterValue: 'audit_ready', tooltip: 'Clients with nothing above Advisory outstanding on their current period.' },
    { key: 'deadlinePassed', title: 'Deadline passed', value: rollup.deadlinePassed, units: rollup.deadlinePassed === 1 ? 'client' : 'clients', filterKey: 'deadline', filterValue: 'passed', tooltip: 'Clients whose reporting deadline has passed and whose period is still unlocked.' },
  ]

  return (
    <PageOuterContainer>
      <PageHeading
        title='Clients'
        includeActionButton={true}
        actionButtonIcon={IconName.Add}
        actionButtonHandler={() => setOpen(true)}
        actionButtonText='Add Client'
      />
      <Typography variant='body1' sx={{ color: 'text.secondary', mb: 3 }}>
        Each client's Report Readiness for its current reporting period, evaluated from the same checks the client sees. Nothing here is ticked by hand.
      </Typography>
      {!isLoading &&
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {tiles.map(tile => {
            const selectedTile = searchParams.get(tile.filterKey) === tile.filterValue
            return (
              <Grid item xl={3} lg={3} md={6} sm={6} xs={12} key={tile.key}
                sx={selectedTile ? { '& .MuiPaper-root': { borderColor: 'primary.main', boxShadow: 1 } } : undefined}>
                <AnalyticMetric
                  compact
                  fitContent
                  title={tile.title}
                  tooltip={tile.tooltip}
                  value={tile.value}
                  units={tile.units}
                  onClick={() => toggleFilterParam(tile.filterKey, tile.filterValue)}
                />
              </Grid>
            )
          })}
        </Grid>}
      <Grid container sx={{ mt: 0, mb: 2 }} rowSpacing={1} columnSpacing={2} alignItems='center'>
        <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ width: { xs: '100%', sm: 280 } }}>
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          </Box>
          <ToggleButtonGroup
            value={readinessFilter}
            exclusive
            size='small'
            onChange={(_event, value) => { if (value !== null) setFilterParam('readiness', value) }}
            aria-label='Filter clients by readiness state'
            sx={segmentedToggleSx}
          >
            <ToggleButton value=''>All states</ToggleButton>
            <ToggleButton value='not_ready'>{READINESS_STATE_LABELS.not_ready}</ToggleButton>
            <ToggleButton value='reportable_with_caveats'>{READINESS_STATE_LABELS.reportable_with_caveats}</ToggleButton>
            <ToggleButton value='audit_ready'>{READINESS_STATE_LABELS.audit_ready}</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={deadlineFilter}
            exclusive
            size='small'
            onChange={(_event, value) => { if (value !== null) setFilterParam('deadline', value) }}
            aria-label='Filter clients by deadline'
            sx={segmentedToggleSx}
          >
            <ToggleButton value=''>All deadlines</ToggleButton>
            <ToggleButton value='passed'>Deadline passed</ToggleButton>
            <ToggleButton value='none'>No deadline set</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={subscriptionFilter}
            exclusive
            size='small'
            onChange={(_event, value) => { if (value !== null) setFilterParam('subscription', value) }}
            aria-label='Filter clients by subscription status'
            sx={segmentedToggleSx}
          >
            <ToggleButton value=''>All statuses</ToggleButton>
            <ToggleButton value='active'>Active</ToggleButton>
            <ToggleButton value='trial'>Trial</ToggleButton>
            <ToggleButton value='inactive'>Not active</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>
      <LoadingPage pageReady={!isLoading}>
        <TableDisplay
          handleSelected={handleSelected}
          selected={selected}
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          setSelected={setSelected}
          totalRecords={rows.length}
          perPage={perPage}
          setPerPage={setPerPage}
          defaultOrderBy='sortKey'
          defaultOrder='asc' />
      </LoadingPage>
      <PartnerAddClientDialog
        open={open}
        closeHandler={() => setOpen(false)}
        successHandler={successHandler}
      />
    </PageOuterContainer>
  )
}
